import * as React from 'react';
import { useMemo } from 'react';
import { Search, UserPlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { RequirePermission } from '@klareospace/pharos-lib-front-utils';
import {
  ActionBar,
  ActionBarDivider,
  ActionBarSpacer,
  Button,
  Input,
  MultiSelect,
} from '@klareospace/pharos-lib-web-utils';
import type { MultiSelectOption } from '@klareospace/pharos-lib-web-utils';
import { USER_STATUS } from '@klareospace/pharos-lib-types';

import { DriverTooltip } from './driver-tooltip';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DriversActionBarProps {
  carrierOptions?: MultiSelectOption[];
  carrierValues?: string[];
  onAdd: () => void;
  onCarrierChange?: (values: string[]) => void;
  onScopeChange?: (values: string[]) => void;
  onSearchChange: (value: string) => void;
  onStatusChange: (values: string[]) => void;
  scopeOptions?: MultiSelectOption[];
  scopeValues?: string[];
  searchValue: string;
  statusValues: string[];
}

// ─── Component ────────────────────────────────────────────────────────────────

export function DriversActionBar({
  carrierOptions,
  carrierValues,
  onAdd,
  onCarrierChange,
  onScopeChange,
  onSearchChange,
  onStatusChange,
  scopeOptions,
  scopeValues,
  searchValue,
  statusValues,
}: DriversActionBarProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('settings');

  const statusOptions = useMemo<MultiSelectOption[]>(
    () => [
      { label: tCommon('statuses.active'), value: USER_STATUS.ACTIVE },
      { label: tCommon('statuses.inactive'), value: USER_STATUS.INACTIVE },
      { label: tCommon('statuses.pending'), value: USER_STATUS.PENDING },
    ],
    [t],
  );

  const multiSelectCommonProps = useMemo(
    () => ({
      emptyText: t('users.multiSelect.noResults'),
      hideClear: true,
      hideClose: true,
      maxCount: 2,
      selectAllText: tCommon('actions.selectAll'),
      showLabelWhenFiltering: true,
      unselectAllText: tCommon('actions.unselectAll'),
    }),
    [t],
  );

  return (
    <ActionBar>
      <div className="relative">
        <Search className="absolute start-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          aria-label={t('drivers.search.ariaLabel')}
          className="h-9 w-[200px] ps-9 text-sm focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring focus-visible:outline-solid"
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={tCommon('filters.searchPlaceholder')}
          value={searchValue}
        />
      </div>
      <ActionBarDivider />
      {scopeOptions && (
        <MultiSelect
          {...multiSelectCommonProps}
          className="h-9 w-auto min-w-[11rem] gap-1.5 border border-input bg-background px-3 text-sm font-normal shadow-none"
          defaultValue={scopeValues ?? []}
          label={tCommon('fields.scope')}
          onValueChange={onScopeChange ?? ((): void => undefined)}
          options={scopeOptions}
          placeholder={tCommon('filters.all')}
          searchable={false}
          variant="compact"
        />
      )}
      {carrierOptions && (
        <MultiSelect
          {...multiSelectCommonProps}
          className="h-9 w-auto min-w-[11rem] gap-1.5 border border-input bg-background px-3 text-sm font-normal shadow-none"
          defaultValue={carrierValues ?? []}
          label={tCommon('fields.carrier')}
          onValueChange={onCarrierChange ?? ((): void => undefined)}
          options={carrierOptions}
          placeholder={tCommon('filters.all')}
          searchable={false}
          variant="compact"
        />
      )}
      <MultiSelect
        {...multiSelectCommonProps}
        className="h-9 w-auto min-w-[11rem] gap-1.5 border border-input bg-background px-3 text-sm font-normal shadow-none"
        defaultValue={statusValues}
        label={tCommon('fields.status')}
        onValueChange={onStatusChange}
        options={statusOptions}
        placeholder={tCommon('filters.all')}
        searchable={false}
        variant="compact"
      />
      <ActionBarSpacer />
      <DriverTooltip />
      <RequirePermission permission="drivers:manage">
        <Button
          className="h-9 gap-1.5 rounded-md px-3 text-sm shadow-sm hover:shadow-md"
          onClick={onAdd}
          type="button"
          variant="default"
        >
          <UserPlus className="h-4 w-4" />
          {t('common.newDriverTitle')}
        </Button>
      </RequirePermission>
    </ActionBar>
  );
}
